/**
 * oct20e LABOR (PR #13 governed remediation) — the governed job-line actions of the ticket card.
 *
 * Row 4c (P4) — FD-2026-10-24-B / CORE-AG :74 (as amended): "A never-started job may be deleted in one click by an
 * authorised user, audited, with financial safeguards and history preserved; once work has begun, or diagnostic
 * evidence or an outcome exists, ordinary one-click Trash is unavailable." The server answers per line
 * (`ticket_job_trash_state`, migration 20270212220000) and enforces the same rule; this control only renders it:
 * an enabled Trash for a never-started line, otherwise a disabled icon with the plain reason.
 *
 * Row 4b (P3) — FD-2026-10-24-B / CORE-AJ :48 (as amended): re-timing a COMPLETED diagnostic is an audited
 * superseding correction (`correct_completed_diagnostic_time`, migration 20270212210000): a manager or owner, a
 * reason, the original kept. Not an approval workflow (R4 :168) — one dialog, one call.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';

// last_job: FD-2026-10-24-C / CORE-Y :42 — a ticket keeps at least one job (migration 20270212330000)
export type JobTrashReason = 'ticket_closed' | 'work_recorded' | 'diagnostic_outcome' | 'diagnostic_evidence' | 'last_job' | 'not_found' | 'not_permitted';

/** Plain-language reasons (P4: "Hide or disable it, with a plain reason"). */
export const JOB_TRASH_REASON_TEXT: Record<JobTrashReason, string> = {
  ticket_closed: 'This ticket is closed, so its jobs cannot be removed.',
  work_recorded: 'Work has started on this job, so it cannot be removed. Its recorded time is kept.',
  diagnostic_outcome: 'This diagnostic has a recorded result, so it cannot be removed.',
  diagnostic_evidence: 'This diagnostic has recorded findings, so it cannot be removed.',
  last_job: 'A ticket needs at least one job. Add the replacement job first, or delete the ticket instead.',
  not_found: 'This job is no longer on the ticket.',
  not_permitted: 'You cannot remove this job.',
};

export interface JobTrashState { allowed: boolean; reason: JobTrashReason | null }

/**
 * The server's per-line answer. `refreshKey` should change whenever the card's job lines change (so a just-started
 * job stops offering Trash). While the answer is loading, a line is treated as NOT removable (fail closed).
 */
export function useJobLineTrashState(ticketId: string, enabled: boolean, refreshKey: unknown) {
  const query = useQuery({
    queryKey: ['ticket-job-trash-state', ticketId, refreshKey],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('ticket_job_trash_state', { p_ticket_id: ticketId });
      if (error) throw error;
      const map = new Map<string, JobTrashState>();
      for (const r of (data ?? []) as Array<{ job_id: string; trash_allowed: boolean; reason_code: JobTrashReason | null }>) {
        map.set(r.job_id, { allowed: r.trash_allowed === true, reason: r.reason_code ?? null });
      }
      return map;
    },
    enabled: enabled && !!ticketId,
    staleTime: 10_000,
  });
  return {
    stateFor: (jobId: string): JobTrashState => query.data?.get(jobId) ?? { allowed: false, reason: null },
    isLoading: query.isLoading,
  };
}

export function JobLineTrashControl({ state, onTrash }: { state: JobTrashState; onTrash: () => void }) {
  if (state.allowed) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Remove job"
        data-testid="job-line-trash"
        onClick={onTrash}
      >
        <Trash2 className="h-3 w-3 text-destructive" />
      </Button>
    );
  }
  const text = state.reason ? JOB_TRASH_REASON_TEXT[state.reason] : 'Checking whether this job can be removed…';
  return (
    <span
      className="inline-flex h-5 w-5 items-center justify-center opacity-0 group-hover:opacity-60 transition-opacity cursor-not-allowed"
      title={text}
      role="img"
      aria-label={text}
      data-testid="job-line-trash-disabled"
      data-reason={state.reason ?? 'loading'}
    >
      <Trash2 className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
    </span>
  );
}

export interface CorrectingJob { id: string; name: string; hours: number }

/** P3: the audited correction of a completed diagnostic's time (manager / owner, reason required). */
export function CompletedDiagnosticCorrectionDialog({
  ticketId, job, canCorrect, onClose, onCorrected,
}: {
  ticketId: string;
  job: CorrectingJob | null;
  canCorrect: boolean;
  onClose: () => void;
  onCorrected: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [hours, setHours] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (job) { setHours(String(job.hours)); setReason(''); }
  }, [job]);

  const minutes = Math.round(parseFloat(hours) * 60);
  const valid = Number.isFinite(minutes) && minutes >= 1 && minutes <= 1440 && reason.trim().length >= 3;

  const submit = async () => {
    if (!job || !valid) return;
    setBusy(true);
    try {
      const { data, error } = await (supabase as any).rpc('correct_completed_diagnostic_time', {
        p_ticket_id: ticketId, p_job_id: job.id, p_minutes: minutes, p_reason: reason.trim(),
      });
      if (error) throw error;
      toast({ title: 'Diagnostic time corrected', description: `${job.name}: ${data?.previous_minutes ?? '?'} → ${minutes} min. The original is kept in the history.` });
      queryClient.invalidateQueries({ queryKey: ['ticket-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-labor-minutes'] });
      onCorrected();
      onClose();
    } catch (e: any) {
      toast({ title: 'Correction not saved', description: String(e?.message ?? e).replace(/^[A-Z_]+: /, ''), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!job} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="completed-diagnostic-correction">
        <DialogHeader>
          <DialogTitle>Correct completed diagnostic time</DialogTitle>
          <DialogDescription>
            {canCorrect
              ? 'This diagnostic is complete. A correction is recorded with your name and reason; the original time stays in the history.'
              : 'This diagnostic is complete. Only a manager or owner can correct its time, with a reason.'}
          </DialogDescription>
        </DialogHeader>
        {job && canCorrect && (
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label className="text-muted-foreground">Job</Label>
              <p className="font-medium">{job.name}</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="correct-diag-hours">Hours</Label>
              <Input id="correct-diag-hours" type="number" step="0.1" min="0.1" value={hours} onChange={(e) => setHours(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="correct-diag-reason">Reason (required)</Label>
              <Textarea id="correct-diag-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this time being corrected?" />
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>{canCorrect ? 'Cancel' : 'Close'}</Button>
          {canCorrect && <Button onClick={submit} disabled={!valid || busy}>Save correction</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
